import DateTimeOrNoData from 'components/content/DateTimeOrNoData';
import type { SortConfig, TableColumn } from 'components/table';
import { DataTable, getNextSortConfig, sortData } from 'components/table';
import { useCan } from 'hooks/useCan';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useListSystemTeamsQuery } from 'services/systemAdmin/teams';
import { getSelectedTeam, setSelectedTeam, setTeamDialog } from 'store/slices/settingsPageSlice';

import type { SystemTeam } from 'API';

const SystemTeamsTable = () => {
    const { data, isLoading, isError, refetch } = useListSystemTeamsQuery('ListSystemTeams', {
        refetchOnMountOrArgChange: true,
    });

    const teams = data ?? [];

    const columns = useMemo<TableColumn<SystemTeam>[]>(
        () => [
            {
                key: 'name',
                title: 'Name',
                dataIndex: 'name',
                sortable: true,
            },
            {
                key: 'numberOfUsers',
                title: 'No. of users',
                dataIndex: 'numberOfUsers',
                sortable: true,
            },
            {
                key: 'numberOfFleets',
                title: 'No. of fleets',
                dataIndex: 'numberOfFleets',
                sortable: true,
            },
            {
                key: 'createdAt',
                title: 'Created',
                dataIndex: 'createdAt',
                sortable: true,
                render: (_, team) => <DateTimeOrNoData value={team.createdAt} />,
                sortValue: team => new Date(team.createdAt ?? ''),
            },
        ],
        []
    );

    const [sortConfig, setSortConfig] = useState<SortConfig<string>>({
        key: 'name',
        dir: 'asc',
    });

    const sortedTeams = useMemo(() => sortData(teams, sortConfig, columns), [teams, sortConfig, columns]);

    const dispatch = useDispatch();
    const selectedTeam = useSelector(getSelectedTeam);

    const { can: canEditTeams } = useCan({ type: 'teams', action: 'edit' });

    return (
        <div className='display-flex flex-column' data-testid='system-teams-table-wrapper'>
            <DataTable
                data-testid='system-teams-table'
                columns={columns}
                dataSource={sortedTeams}
                stickyHeader
                rowKey='id'
                sortConfig={sortConfig}
                onSortChange={key => setSortConfig(prev => getNextSortConfig(prev, key))}
                onRowClick={team => dispatch(setSelectedTeam(team))}
                activeRowKey={selectedTeam?.id}
                isLoading={isLoading}
                loadMore={{
                    loaded: teams.length,
                    total: teams.length,
                }}
                isError={isError}
                actionColumn={{
                    ariaLabel: team => `Actions for ${team.name}`,
                    disabled: !canEditTeams,
                    actions: team =>
                        canEditTeams
                            ? [
                                  {
                                      id: 'edit',
                                      title: 'Edit',
                                      icon: 'rioglyph-pencil',
                                      onSelect: () => dispatch(setTeamDialog({ team, showTeamDialog: true })),
                                  },
                              ]
                            : [],
                }}
                error={{
                    header: 'Failed to load teams',
                    message: 'If this issue persists, please contact the Control Center support team.',
                    onClick: () => refetch(),
                }}
                empty={{
                    title: 'No teams yet, but they will appear here',
                    body: 'As of now, there are no teams in the Control Center.',
                }}
            />
        </div>
    );
};

export default SystemTeamsTable;
